import { useForm } from 'react-hook-form'
import { ReactElement, useCallback } from 'react'

import { format } from 'date-fns'
import { z } from 'zod';
import { parse, isValid } from 'date-fns';

import { Botao, Cores, Corpo, Icone, InputData, Textfield } from '@componentes-mu/componentes-mu'
import { zodResolver } from '@hookform/resolvers/zod';

import styles from './styles.module.scss'
import { Column } from '../../components/Table/types'
import Table from '../../components/Table'
import { Treinamento, TreinamentosFilters, TreinamentosResponse } from '../../types/cliente/treinamentos/types'
import { FlexRow } from '../../components/Flex'

const schema = z.object({
  dataInicial: z.string().optional(),
  dataFinal: z.string().optional(),
  tipoOperacao: z.string().optional(),
  nomeTreinamento: z.string().optional(),
  descricaoTreinamento: z.string().optional(),
  pagina: z.number().optional(),
  tamanhoPagina: z.number().optional(),
}).refine((data) => {
  if (data.dataInicial && data.dataFinal) {
    const parsedDataInicial = parse(data.dataInicial, 'dd/MM/yyyy', new Date());
    const parsedDataFinal = parse(data.dataFinal, 'dd/MM/yyyy', new Date());
    return isValid(parsedDataInicial) && isValid(parsedDataFinal) && parsedDataFinal >= parsedDataInicial;
  }
  return true;
}, {
  message: "Data final deve ser maior que a data inicial",
  path: ["dataFinal"],
});


type TreinamentosProps = {
  data: TreinamentosResponse,
  filters: TreinamentosFilters
  onPageChange: (page: number) => void,
  onItemsPerPageChange: (itemsPerPage: number) => void,
  onFilter: (data: TreinamentosFilters) => void
}

const Treinamentos = ({ data, filters, onPageChange, onItemsPerPageChange, onFilter }: TreinamentosProps) => {

  const { setValue, watch, handleSubmit,  formState: { errors } } = useForm<TreinamentosFilters>({
    defaultValues: {
      ...filters
    },
    resolver: zodResolver(schema),
  });

  const renderContainer = useCallback((conatiner: string, original: Treinamento) => {
    const conatinerOnj: Record<string, ReactElement> = {
      data: (
        <>{format(new Date(original.data), 'dd/MM/yyyy')}</>
      ),
      chaveS3: (
        <a
          href={original.chaveS3}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none", }}
          title="Reproduzir vídeo"
        >
          <Botao
            estilo="ghost"
            tamanho="xsmall"
          >
            <Icone
              nome="play_arrow"
              cor={Cores.GradacaoNeutros.CinzaFillMU500}
              altura="20px"
              largura="20px"
            />
            Reproduzir vídeo

          </Botao>
        </a>
      )
    }

    return conatinerOnj[conatiner]
  }, []);

  const columns: Column<Treinamento>[] = [
    {
      key: "nome",
      header: "NOME",
      width: '25%',
      align: "left",
    },
    {
      key: "descricao",
      header: "DESCRIÇÃO",
      width: '55%',
      align: "left",
    },
    {
      key: "data",
      header: "DATA",
      Cell: (row) => renderContainer('data', row),
      width: '10%'
    },
    {
      key: "chaveS3",
      header: "VÍDEO",
      Cell: (row) => renderContainer('chaveS3', row),
      width: '10%'
    },
  ];

  return (
    <>
      <form className={styles.formContainer} onSubmit={handleSubmit(onFilter)}>
        <FlexRow alignItems='center' gap='4rem'>

          <FlexRow alignItems='center' gap='4rem' justifyContent='center'>

            <Textfield
              id='nomeTreinamento'
              placeholder={"Título do treinamento"}
              valor={watch('nomeTreinamento')}
              onChange={(e) => setValue("nomeTreinamento", e || '')}
              style={{ width: "100%" }}
            />

            <FlexRow alignItems='center' gap='1rem' justifyContent='center' width={"50%"}>
              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600} negrito={500}>
                Périodo
              </Corpo>

              <div style={{ maxWidth: "200px" }}>
                <InputData
                  id='dataInicial'
                  onChange={(e) => setValue("dataInicial", e || undefined)}
                  label={false}
                  placeholder={true}
                  formatoData="dd/mm/aaaa"
                  value={watch('dataInicial')}
                />
              </div>

              <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600} negrito={500}>até</Corpo>

              <div style={{ maxWidth: "200px" }}>
                <InputData
                  id='datafinal'
                  onChange={(e) => setValue("dataFinal", e || undefined)}
                  label={false}
                  placeholder={true}
                  formatoData="dd/mm/aaaa"
                  value={watch('dataFinal')}
                  variante={errors.dataFinal ? 'error' : 'regular'}
                />
              </div>
            </FlexRow>
          </FlexRow>

          <Botao tipoComportamento="submit" cor={Cores.GradacaoNeutros.CinzaFillMU100}
            tipo='tipoDefault'
            style={{ minWidth: '100px' }}
          >
            <Corpo tipo="button1" cor={Cores.CoresIdentidadeMU.azulMu} negrito={700}>
              Buscar
            </Corpo>
          </Botao>

        </FlexRow>
      </form >

      <Table
        columns={columns}
        data={data.treinamentos}
        currentPage={data.paginaAtual}
        itemsPerPage={data.tamanhoPagina}
        totalItems={data.totalItens}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
      />
    </>
  )
}

export { Treinamentos }
